import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { payWithMidtransPublic } from "@/lib/midtrans";
import { formatRp, formatRupiahInput, parseRupiah } from "@/lib/constants";
import { CreditCard, House, Storefront, CheckCircle } from "@phosphor-icons/react";

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";

function Row({ l, v, bold }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-slate-500">{l}</span>
      <span className={bold ? "font-extrabold text-[#7C4DE0]" : "font-semibold text-slate-800"}>{v}</span>
    </div>
  );
}

export default function PublicOrderPay() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ configured: false, is_production: false });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/order/${token}`);
      setO(data);
      const sisa = Math.round(data.sisa_tagihan || 0);
      const dpReq = Math.round(data.dp_required || 0);
      const paid = Math.round(data.total_dibayar || 0);
      const def = data.payment_term === "dp" ? (Math.max(0, dpReq - paid) || sisa) : sisa;
      setAmount(formatRupiahInput(String(def)));
    } catch (e) { setErr(apiError(e) || "Pesanan tidak ditemukan"); }
  }, [token]);

  useEffect(() => {
    load();
    api.get("/public/payments/midtrans/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, [load]);

  const pay = async () => {
    if (busy) return;
    const amt = parseRupiah(amount);
    if (!amt || amt <= 0) return toast.error("Nominal tidak valid");
    if (amt > Math.round(o.sisa_tagihan)) return toast.error("Nominal melebihi sisa tagihan");
    setBusy(true);
    try {
      const res = await payWithMidtransPublic({ token, amount: amt });
      if (res.status === "success") toast.success("Pembayaran berhasil");
      else if (res.status === "pending") toast.info("Pembayaran menunggu penyelesaian");
      else if (res.status === "error") toast.error("Pembayaran gagal / ditolak");
      try { await api.post(`/public/payments/midtrans/order/${token}/reconcile`); } catch (_) { /* best-effort */ }
      await load();
    } catch (e) { toast.error(apiError(e) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDF2F8]">
      <header className="border-b border-rose-100/70 bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4 sm:px-6">
          <CreditCard size={26} weight="duotone" className="text-[#7C4DE0]" />
          <p className="font-heading text-sm font-extrabold text-slate-800">Pembayaran Pesanan GBS</p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        {err ? (
          <div data-testid="pop-error" className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
            <h1 className="font-heading text-xl font-extrabold text-slate-800">Pesanan Tidak Ditemukan</h1>
            <p className="mt-2 text-slate-600">{err}</p>
          </div>
        ) : !o ? (
          <p className="text-center text-slate-400">Memuat…</p>
        ) : (
          <div className="space-y-4" data-testid="pop-order">
            <div className="rounded-2xl border border-[#E0D2FF] bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="font-heading text-lg font-extrabold text-slate-800" data-testid="pop-order-no">{o.order_no}</p>
                <span className="rounded-full bg-[#EEE6FF] px-3 py-1 text-xs font-bold text-[#6A38C7]" data-testid="pop-status">{o.payment_status}</span>
              </div>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                {o.lokasi === "Homecare" ? <House size={16} /> : <Storefront size={16} />}
                {o.lokasi}{o.alamat ? ` · ${o.alamat}` : ""} · {o.tanggal_kunjungan}
              </p>
              <p className="mt-0.5 text-sm text-slate-500">a/n {o.nama_kontak}</p>
            </div>

            <div className="rounded-2xl border border-[#E0D2FF] bg-white p-4">
              <p className="mb-2 font-heading font-bold text-slate-800">Rincian Layanan</p>
              <div className="space-y-2">
                {(o.items || []).map((it, i) => (
                  <div key={i} data-testid={`pop-item-${i}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{it.jenis_layanan}</p>
                      <p className="text-xs text-slate-400">{it.nama_pasien}{it.jam_kunjungan ? ` · ${it.jam_kunjungan}` : ""}{it.petugas_nama ? ` · ${it.petugas_nama}` : ""}</p>
                    </div>
                    <span className="text-sm font-bold text-slate-700">{formatRp(it.total_tagihan)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#E0D2FF] bg-[#FAF7FF] p-4">
              <Row l="Subtotal" v={formatRp(o.subtotal)} />
              {o.visit_fee > 0 && <Row l="Visit fee" v={formatRp(o.visit_fee)} />}
              {o.diskon_nominal > 0 && <Row l="Diskon" v={`− ${formatRp(o.diskon_nominal)}`} />}
              <Row l="Grand Total" v={formatRp(o.grand_total)} bold />
              {o.payment_term === "dp" && o.dp_required > 0 && <Row l="DP Diminta" v={formatRp(o.dp_required)} />}
              <Row l="Sudah Dibayar" v={formatRp(o.total_dibayar)} />
              <Row l="Sisa Tagihan" v={formatRp(o.sisa_tagihan)} />
            </div>

            {o.payment_status === "Expired" || o.order_status === "Batal" ? (
              <div data-testid="pop-expired" className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-center text-sm font-bold text-rose-700">
                Pesanan dibatalkan karena pembayaran kedaluwarsa (lewat 1 jam). Silakan buat pesanan baru atau hubungi Admin GBS.
              </div>
            ) : o.is_estimate ? (
              <div data-testid="pop-pending" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center text-sm font-semibold text-amber-700">
                Tagihan masih menunggu konfirmasi Admin. Link pembayaran akan aktif setelah tagihan dikonfirmasi.
              </div>
            ) : Math.round(o.sisa_tagihan || 0) <= 0 ? (
              <div data-testid="pop-paid" className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
                <CheckCircle size={18} weight="fill" /> Pembayaran Lunas. Terima kasih!
              </div>
            ) : !cfg.configured ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center text-sm text-slate-500">
                Pembayaran online belum tersedia. Silakan hubungi Admin GBS.
              </div>
            ) : (
              <div className="rounded-2xl border border-[#C7D2FE] bg-[#EEF2FF] p-4">
                {!cfg.is_production && (
                  <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700" data-testid="pop-sandbox-note">
                    Mode uji (SANDBOX) — gunakan kartu/simulator Midtrans.
                  </p>
                )}
                <div className="flex gap-2">
                  <input data-testid="pop-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(formatRupiahInput(e.target.value))} placeholder="Nominal" className={inp} />
                  <button data-testid="pop-pay-btn" onClick={pay} disabled={busy} className="shrink-0 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50">
                    {busy ? "Memproses…" : "Bayar via Midtrans"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
